import Container from './Container';
import { useState, useRef, useEffect } from 'react';

// Navigation: minimal, dropdown for Soluzioni
export default function Navigation() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [soluzioniOpen, setSoluzioniOpen] = useState(false);
  const [soluzioniDesktopOpen, setSoluzioniDesktopOpen] = useState(false);
  const desktopDropdownRef = useRef(null);

  useEffect(() => {
    if (!soluzioniDesktopOpen) return;
    const handleClickOutside = (e) => {
      if (desktopDropdownRef.current && !desktopDropdownRef.current.contains(e.target)) {
        setSoluzioniDesktopOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [soluzioniDesktopOpen]);

  return (
    <header className="sticky top-0 z-50 bg-warm-white/70 backdrop-blur-md border-b border-taupe-light/30 shadow-sm transition-all duration-300">
      <Container>
        <nav className="flex items-center justify-between py-4 lg:py-5">
          <a href="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity flex-shrink-0">
            <img src="/logo-ottica-bellodi.svg" alt="Ottica Bellodi" width="200" height="50" className="h-10 lg:h-12 w-auto" />
          </a>
          
          {/* Desktop Menu */}
          <ul className="hidden md:flex gap-6 lg:gap-9 text-sm lg:text-base font-sans font-medium tracking-[0.05em] items-center">
            <li>
              <a href="/" className="text-primary no-underline relative py-2 after:absolute after:bottom-0 after:left-0 after:w-0 after:h-0.5 after:bg-accent after:transition-all after:duration-300 hover:after:w-full">
                Home
              </a>
            </li>
            <li className="relative group" ref={desktopDropdownRef}>
              <span className="flex items-center gap-1">
                <a href="/soluzioni/" className="text-primary no-underline relative py-2 after:absolute after:bottom-0 after:left-0 after:w-0 after:h-0.5 after:bg-accent after:transition-all after:duration-300 hover:after:w-full">
                  Soluzioni
                </a>
                <button
                  onClick={() => setSoluzioniDesktopOpen(!soluzioniDesktopOpen)}
                  className="text-primary hover:text-accent transition-colors p-1 rounded"
                  aria-label="Mostra sottomenu"
                >
                  <svg className={`w-3.5 h-3.5 transition-transform duration-200 ${soluzioniDesktopOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
              </span>
              <ul className={`absolute left-0 top-full mt-2 bg-white shadow-lg rounded-lg py-2 min-w-[240px] border border-taupe-light/30 before:absolute before:content-[''] before:left-0 before:right-0 before:-top-2 before:h-2 ${soluzioniDesktopOpen ? 'block' : 'hidden group-hover:block'}`}>
                <li><a href="/occhiali-da-vista/" className="block px-4 py-2 text-primary no-underline hover:bg-accent/10 transition-colors">Occhiali da Vista</a></li>
                <li><a href="/occhiali-da-sole/" className="block px-4 py-2 text-primary no-underline hover:bg-accent/10 transition-colors">Occhiali da Sole</a></li>
                <li><a href="/lenti-a-contatto/" className="block px-4 py-2 text-primary no-underline hover:bg-accent/10 transition-colors">Lenti a Contatto</a></li>
                <li><a href="/montature-stampate-3d/" className="block px-4 py-2 text-primary no-underline hover:bg-accent/10 transition-colors">Montature Stampate 3D</a></li>
                <li><a href="/lenti-progressive-personalizzate/" className="block px-4 py-2 text-primary no-underline hover:bg-accent/10 transition-colors">Anima — Lenti Matrice Digitale</a></li>
              </ul>
            </li>
            <li>
              <a href="/catalogo/" className="text-primary no-underline relative py-2 after:absolute after:bottom-0 after:left-0 after:w-0 after:h-0.5 after:bg-accent after:transition-all after:duration-300 hover:after:w-full">
                Catalogo
              </a>
            </li>
            <li>
              <a href="/chi-siamo/" className="text-primary no-underline relative py-2 whitespace-nowrap after:absolute after:bottom-0 after:left-0 after:w-0 after:h-0.5 after:bg-accent after:transition-all after:duration-300 hover:after:w-full">
                Chi Siamo
              </a>
            </li>
            <li>
              <a href="/blog/" className="text-primary no-underline relative py-2 after:absolute after:bottom-0 after:left-0 after:w-0 after:h-0.5 after:bg-accent after:transition-all after:duration-300 hover:after:w-full">
                Blog
              </a>
            </li>
            <li>
              <a href="/contatti/" className="text-primary no-underline relative py-2 after:absolute after:bottom-0 after:left-0 after:w-0 after:h-0.5 after:bg-accent after:transition-all after:duration-300 hover:after:w-full">
                Contatti
              </a>
            </li>
            <li>
              <a href="/offerte/" className="bg-white text-accent text-sm font-semibold px-4 py-2 rounded-full no-underline border border-accent hover:bg-accent hover:text-white transition-colors whitespace-nowrap">
                Offerta Ottobre
              </a>
            </li>
          </ul>
          
          {/* Mobile menu button */}
          <button 
            className="md:hidden text-primary hover:text-accent transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 p-1 rounded-md"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-expanded={mobileMenuOpen}
            aria-controls="mobile-menu"
            aria-label={mobileMenuOpen ? "Chiudi menu di navigazione principale" : "Apri menu di navigazione principale"}
          >
            {mobileMenuOpen ? (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            )}
          </button>
        </nav>
        
        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div id="mobile-menu" className="md:hidden pb-4 border-t border-taupe-light/30 mt-2 animate-fadeIn bg-white/50 backdrop-blur-lg rounded-b-xl px-4">
            <ul className="flex flex-col gap-2 pt-4">
              <li>
                <a href="/" className="block py-2 text-primary no-underline hover:text-accent transition-colors">
                  Home
                </a>
              </li>
              <li>
                <button 
                  onClick={() => setSoluzioniOpen(!soluzioniOpen)}
                  className="w-full text-left py-2 text-primary flex items-center justify-between hover:text-accent transition-colors"
                >
                  Soluzioni
                  <svg 
                    className={`w-4 h-4 transition-transform ${soluzioniOpen ? 'rotate-180' : ''}`}
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {soluzioniOpen && (
                  <ul className="pl-4 mt-2 space-y-2">
                    <li><a href="/occhiali-da-vista/" className="block py-2 text-taupe no-underline hover:text-accent transition-colors">Occhiali da Vista</a></li>
                    <li><a href="/occhiali-da-sole/" className="block py-2 text-taupe no-underline hover:text-accent transition-colors">Occhiali da Sole</a></li>
                    <li><a href="/lenti-a-contatto/" className="block py-2 text-taupe no-underline hover:text-accent transition-colors">Lenti a Contatto</a></li>
                    <li><a href="/montature-stampate-3d/" className="block py-2 text-taupe no-underline hover:text-accent transition-colors">Montature Stampate 3D</a></li>
                    <li><a href="/lenti-progressive-personalizzate/" className="block py-2 text-taupe no-underline hover:text-accent transition-colors">Anima — Lenti Matrice Digitale</a></li>
                  </ul>
                )}
              </li>
              <li>
                <a href="/catalogo/" className="block py-2 text-primary no-underline hover:text-accent transition-colors">
                  Catalogo
                </a>
              </li>
              <li>
                <a href="/chi-siamo/" className="block py-2 text-primary no-underline hover:text-accent transition-colors">
                  Chi Siamo
                </a>
              </li>
              {/* Galleria rimossa dal menu mobile */}
              <li>
                <a href="/blog/" className="block py-2 text-primary no-underline hover:text-accent transition-colors">
                  Blog
                </a>
              </li>
              <li>
                <a href="/contatti/" className="block py-2 text-primary no-underline hover:text-accent transition-colors">
                  Contatti
                </a>
              </li>
              <li>
                <a href="/offerte/" className="inline-block mt-2 bg-white text-accent text-sm font-semibold px-4 py-2 rounded-full no-underline border border-accent hover:bg-accent hover:text-white transition-colors">
                  Offerta Ottobre
                </a>
              </li>
            </ul>
          </div>
        )}
      </Container>
    </header>
  );
}